import { Country } from '@prisma/client';
import { SalesTaxJob } from '../job/types';

interface IndirectTaxCorporationData {
  id: string;
  legal_name: string;
  created_at: number;
  jurisdiction?: string;
  email_alias?: string;
  legacy_email_alias?: string;
}

export interface IndirectTaxCorporationDataResponse {
  data: IndirectTaxCorporationData;
  message: string;
}

export interface EmailAliasUpdatePayload {
  emails: string[];
}

export interface EmailAliasUpdateResponse {
  data: {
    email_alias: string;
  };
  message: string;
}

export interface EmailAliasGetResponse {
  data: {
    emails: string[];
  };
  message: string;
}

export enum SalesTaxSettingsTypes {
  SALES_TAX_SETTINGS = 'SALES_TAX_SETTINGS',
  SYNC_SETTINGS = 'SYNC_SETTINGS',
  CALCULATION_SETTINGS = 'CALCULATION_SETTINGS',
}

export enum CalculationsAfterNexusBreach {
  NEVER = 'NEVER',
  IMMEDIATELY = 'IMMEDIATELY',
}

export enum ExemptionExpirationPolicy {
  IMMEDIATELY = 'IMMEDIATELY',
  NEVER = 'NEVER',
  AFTER_N_DAYS = 'AFTER_N_DAYS',
}

export interface CorporationAvailableSettings {
  enable_calculations_after_nexus_breach?: CalculationsAfterNexusBreach;
  default_product_taxability_code?: string;
  filing_approval_configuration?: {
    auto_approval: 'DISABLED' | 'ENABLED';
    auto_approval_due_day: number;
  };
  countries_to_sync?: string[];
}

export interface CorporationSettingsConfiguration {
  data: CorporationAvailableSettings;
}

export interface SyncSettingsData {
  countries_to_sync?: string[];
}

export interface SyncSettingsConfiguration {
  data: SyncSettingsData;
}

export interface CalculationSettingsData {
  countries_to_calculate_tax?: string[];
}

export interface CalculationSettingsConfiguration {
  data: CalculationSettingsData;
}

export type CorporationSettings =
  | {
      country: Country;
      type: SalesTaxSettingsTypes.SALES_TAX_SETTINGS;
      configuration: CorporationSettingsConfiguration;
    }
  | {
      country: Country;
      type: SalesTaxSettingsTypes.SYNC_SETTINGS;
      configuration: SyncSettingsConfiguration;
    }
  | {
      country: Country;
      type: SalesTaxSettingsTypes.CALCULATION_SETTINGS;
      configuration: CalculationSettingsConfiguration;
    };

export interface ExemptionExpirationConfig {
  policy: ExemptionExpirationPolicy;
  duration?: number; // Optional, only used when policy is AFTER_N_DAYS
}

export interface JurisdictionAvailableSettings {
  disable_tax_calculation?: boolean;
  manage_filings?: boolean;
  disable_exemptions_after_expiration?: ExemptionExpirationConfig;
}

export interface JurisdictionSettingsConfiguration {
  type: SalesTaxSettingsTypes.SALES_TAX_SETTINGS;
  data: JurisdictionAvailableSettings;
}

export interface CorporationJurisdictionSettings {
  configuration: JurisdictionSettingsConfiguration;
}

export interface AllSettingsData {
  enable_calculations_after_nexus_breach: CalculationsAfterNexusBreach;
  manage_filings: boolean;
  disable_tax_calculation: boolean;
  disable_exemptions_after_expiration: ExemptionExpirationConfig;
  default_product_taxability_code: string;
  filing_approval_configuration: {
    auto_approval: 'DISABLED' | 'ENABLED';
    auto_approval_due_day: number;
  };
  countries_to_sync?: string[];
  countries_to_calculate_tax?: string[];
  target_currency?: string;
  upload_method?: string;
  integration_country_sync?: Record<string, string[]>;
}

export interface SettingsData {
  corporation_id: string;
  data: AllSettingsData;
  type: SalesTaxSettingsTypes;

  // TODO: fix this when API is updated.
  state?: string | 'COUNTRYWIDE'; // this is the state or countrywide
  country: Country;
}

export interface SettingsGetResponse {
  data: SettingsData[];
  message: string;
}

export interface GenerateTaxBreakdownResponse {
  data: {
    job_id: string;
  };
  message: string;
}

export interface GenerateInvoiceTaxBreakdownResponse {
  data: {
    message: string;
  };
  message: string;
}

export interface TriggerSyncFilingsResponse {
  data: {
    message: string;
  };
  message: string;
}

export interface GenerateTaxBreakdownForFilingsParams {
  filing_id?: string;
  due_date_start?: string; // Format: YYYY-MM-DD
  due_date_end?: string; // Format: YYYY-MM-DD
}

export interface SyncFilingNumbersQueryParams {
  due_date_start?: string; // Format: YYYY-MM-DD
  due_date_end?: string; // Format: YYYY-MM-DD
}

export type FilingSyncProgressBreakdown = {
  filing_id: string;
  filing_name: string;
  filing_frequency: string;
  status: string;
  filing_due_date: string;
  state: string;
  country: string;
  tax_period_start_date: string;
  tax_period_end_date: string;
  total_invoices_in_range: number;
  total_invoices_locked: number;
  total_breakdowns_not_started: number;
  total_breakdowns_queued: number;
  total_breakdowns_in_progress: number;
  total_breakdowns_completed: number;
  total_breakdowns_failed: number;
};

export interface FilingsSyncProgressResponse {
  data: {
    filings: FilingSyncProgressBreakdown[];
  };
  message: string;
}

export interface FilingSyncInProgressParams {
  filing_id?: string;
  due_date_start?: string; // Format: YYYY-MM-DD
  due_date_end?: string; // Format: YYYY-MM-DD
}

export interface FilingAggregateJobsParams {
  due_date_start?: string; // Format: YYYY-MM-DD
  due_date_end?: string; // Format: YYYY-MM-DD
}

export interface FilingAggregationJobsResponse {
  jobs: SalesTaxJob[];
}
